/**
 * Gate 5 — centralized organization query scoping (application layer).
 * RLS remains Gate 6. Schema rollout was Gate 4.
 *
 * Pattern: resolve org id → apply scope → Supabase query builder.
 *
 * RG-06 — Fail-closed organization resolution:
 * If organization ownership cannot be positively established for an org-scoped
 * table, never omit the organization filter (no unscoped SELECT/INSERT).
 *
 * Canonical hierarchy (operational queries):
 *   1. explicitOrganizationId (caller-provided)
 *   2. actor.organization_id (membership enrichment)
 *   3. FAIL — do not guess, do not substitute another organization
 *
 * Intentional singleton default (`cp_get_default_organization_id`) is only used
 * when `allowDefaultOrganization: true` (invitations / membership tie-break /
 * documented platform singleton settings — not Gate 4 operational queries).
 */

import { GATE4_ROLLOUT_TABLES } from "./orgRolloutGate4";
import {
  getDefaultOrganizationId,
  resolveOrganizationIdFromActor,
} from "./organizationHelpers";
import { RG06_ORG_REQUIRED_ERROR_CODE } from "./orgResolutionIntegrity";

export const ORG_SCOPED_OPERATIONAL_TABLES = Object.freeze(
  new Set(GATE4_ROLLOUT_TABLES),
);

/**
 * Tables with organization_id that are intentionally excluded from automatic
 * read scoping (global reference data or child rows scoped via parent FK).
 */
export const ORG_SCOPE_READ_EXCEPTIONS = Object.freeze(
  new Set([
    "invoice_line_items",
    "estimate_rooms",
    "estimate_features",
    "estimate_room_sections",
    "estimate_room_materials",
    "estimate_room_labour",
    "project_material_commit_batches",
    // RG-04 Phase B: catalog ownership uses applyCatalogVisibilityScope
    // (platform OR actor org) — never plain organization_id eq alone.
    "feature_types",
    "assembly_templates",
    "work_type_templates",
  ]),
);

/** Matches no real organization — used to deny unscoped Gate 4 queries (RG-06). */
export const ORG_SCOPE_DENY_SENTINEL =
  "00000000-0000-0000-0000-000000000000";

export function isOrgScopedOperationalTable(tableName) {
  return ORG_SCOPED_OPERATIONAL_TABLES.has(tableName);
}

export function shouldApplyOrganizationReadScope(tableName) {
  return (
    isOrgScopedOperationalTable(tableName) &&
    !ORG_SCOPE_READ_EXCEPTIONS.has(tableName)
  );
}

/**
 * Resolve organization id for queries.
 *
 * @param {object|null} actor
 * @param {object} client
 * @param {string|null} explicitOrganizationId
 * @param {{ allowDefaultOrganization?: boolean }} [options]
 *   When true, unauthenticated / no-actor callers may use the platform default
 *   org RPC. Authenticated actors never receive a silent default.
 */
export async function resolveOrganizationIdForQueries(
  actor,
  client,
  explicitOrganizationId = null,
  options = {},
) {
  if (explicitOrganizationId) {
    return explicitOrganizationId;
  }

  const fromActor = resolveOrganizationIdFromActor(actor);
  if (fromActor) {
    return fromActor;
  }

  // Authenticated without membership org → fail-closed (null). Never substitute.
  if (actor?.supabase_user_id) {
    return null;
  }

  if (options.allowDefaultOrganization) {
    return getDefaultOrganizationId(client);
  }

  // Guest / missing actor: no positive ownership → null (callers must not unscope).
  return null;
}

/**
 * Apply `.eq('organization_id', …)` for org-scoped tables.
 * RG-06: when organizationId is missing, apply deny sentinel — never omit the filter.
 */
export function applyOrganizationScope(
  query,
  organizationId,
  { tableName = null } = {},
) {
  if (!query) {
    return query;
  }

  if (tableName && !shouldApplyOrganizationReadScope(tableName)) {
    return query;
  }

  // When tableName is omitted, still require an org id for any eq — deny if missing.
  if (!organizationId) {
    return query.eq("organization_id", ORG_SCOPE_DENY_SENTINEL);
  }

  return query.eq("organization_id", organizationId);
}

/**
 * Merge organization_id onto a single insert/update payload when absent.
 */
export function stampOrganizationId(record, organizationId) {
  if (!record || !organizationId || record.organization_id) {
    return record;
  }

  return { ...record, organization_id: organizationId };
}

export function stampOrganizationIds(records, organizationId) {
  if (!Array.isArray(records)) {
    return stampOrganizationId(records, organizationId);
  }

  return records.map((row) => stampOrganizationId(row, organizationId));
}

/**
 * Start a SELECT on an org-scoped table with organization filter applied.
 */
export async function beginOrgScopedSelect(
  client,
  tableName,
  select,
  actor,
  options = {},
) {
  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    options.organizationId,
    { allowDefaultOrganization: options.allowDefaultOrganization },
  );

  let query = client.from(tableName).select(select, options.selectOptions);
  query = applyOrganizationScope(query, organizationId, { tableName });

  return { query, organizationId };
}

/**
 * Insert one or more rows with organization_id stamped when missing.
 * RG-06: fail closed when organization cannot be resolved.
 */
export async function orgScopedInsert(
  client,
  tableName,
  records,
  actor,
  options = {},
) {
  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    options.organizationId,
    { allowDefaultOrganization: options.allowDefaultOrganization },
  );

  if (!organizationId) {
    return Promise.resolve({
      data: null,
      error: createOrganizationRequiredError(`${tableName} insert`),
    });
  }

  const payload = stampOrganizationIds(records, organizationId);

  let builder = client.from(tableName).insert(payload);

  if (options.select) {
    builder = builder.select(options.select);
  }

  if (options.single) {
    builder = builder.single();
  }

  return builder;
}

/**
 * Require a resolved organization id for scoped mutates (RG-03 / RG-06).
 * Fail closed: never return an unscoped PK-only update builder.
 */
export function createOrganizationRequiredError(context = "scoped write") {
  return {
    message: `organization_id is required for ${context}`,
    code: RG06_ORG_REQUIRED_ERROR_CODE,
  };
}

/**
 * Update rows filtered by primary key AND organization_id.
 * Returns a thenable Postgrest builder, or `{ data: null, error }` when org cannot be resolved.
 */
export async function orgScopedUpdateByPk(
  client,
  tableName,
  payload,
  actor,
  { pkColumn, pkValue, organizationId: explicitOrganizationId = null } = {},
) {
  if (!client || !tableName || !pkColumn || pkValue == null) {
    return {
      data: null,
      error: {
        message: "orgScopedUpdateByPk requires client, table, pkColumn, and pkValue",
        code: "RG03_INVALID_ARGS",
      },
    };
  }

  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    explicitOrganizationId,
  );

  if (!organizationId) {
    return { data: null, error: createOrganizationRequiredError(`${tableName} update`) };
  }

  return client
    .from(tableName)
    .update(payload)
    .eq(pkColumn, pkValue)
    .eq("organization_id", organizationId);
}

/**
 * Update rows filtered by primary-key IN list AND organization_id.
 */
export async function orgScopedUpdateByPkIn(
  client,
  tableName,
  payload,
  actor,
  { pkColumn, pkValues, organizationId: explicitOrganizationId = null } = {},
) {
  const ids = [...new Set((pkValues || []).filter(Boolean))];
  if (!client || !tableName || !pkColumn || !ids.length) {
    return { data: null, error: null };
  }

  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    explicitOrganizationId,
  );

  if (!organizationId) {
    return { data: null, error: createOrganizationRequiredError(`${tableName} batch update`) };
  }

  return client
    .from(tableName)
    .update(payload)
    .in(pkColumn, ids)
    .eq("organization_id", organizationId);
}

/**
 * Apply organization scope to an existing query builder (reads / updates / deletes).
 *
 * Returns a Postgrest builder (thenable). Do not chain `.eq()` / `.order()` after
 * `await applyOrganizationScopeForActor(...)` — awaiting executes the query.
 * Resolve org id first, then use sync `applyOrganizationScope`, or use a loadScoped*
 * adapter that chains filters before the final await.
 */
export async function applyOrganizationScopeForActor(
  query,
  actor,
  client,
  options = {},
) {
  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    options.organizationId,
    { allowDefaultOrganization: options.allowDefaultOrganization },
  );

  return applyOrganizationScope(query, organizationId, {
    tableName: options.tableName,
  });
}
